import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container,
  Button,
  Paper,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

// Get backend URL from environment or auto-detect from current URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  if (typeof window !== 'undefined') {
    return `http://${window.location.hostname}:5000`;
  }
  return '';
};
const API_BASE_URL = getApiBaseUrl();

const KioskInitialize = () => {
  const navigate = useNavigate();
  const [cacheStatus, setCacheStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleContinue = useCallback((e) => {
    e?.preventDefault();
    console.log('Attempting to navigate to /shop');
    
    // Try programmatic navigation first
    navigate('/shop', { replace: true });
    
    // Fallback to window.location if needed
    setTimeout(() => {
      if (window.location.pathname !== '/shop') {
        console.log('Programmatic navigation failed, using window.location');
        window.location.href = '/shop';
      }
    }, 100);
  }, [navigate]);

  useEffect(() => {
    const fetchCacheStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cache-status`);
        if (!response.ok) throw new Error('Failed to fetch cache status');
        const data = await response.json();
        console.log('Cache status data:', JSON.stringify(data, null, 2));
        setCacheStatus(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCacheStatus();
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      // If timestamp is a number, it's already in milliseconds
      // If it's a string, it might be an ISO string
      const date = typeof timestamp === 'number' 
        ? new Date(timestamp) 
        : new Date(timestamp);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', timestamp);
        return 'Invalid date';
      }
      
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Error';
    }
  };

  const getStatusIcon = (isValid) => (
    isValid ? 
      <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : 
      <ErrorIcon color="error" sx={{ mr: 1 }} />
  );

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading cache status...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" color="error">Error loading cache status</Typography>
        <Typography color="textSecondary" sx={{ mt: 2, mb: 4 }}>{error}</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <ShoppingCartIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h4" gutterBottom>Welcome to Our Store</Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ textAlign: 'left', mb: 4 }}>
          <Typography variant="h6" gutterBottom>Cache Status</Typography>
          
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                {getStatusIcon(cacheStatus.products.ready)}
                Products Cache
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                Last Updated: {formatTimestamp(cacheStatus.products.lastUpdated)}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                Items: {cacheStatus.products.count || 0}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                {getStatusIcon(cacheStatus.users.ready)}
                Users Cache
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                Last Updated: {formatTimestamp(cacheStatus.users.lastUpdated)}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                Items: {cacheStatus.users.count || 0}
              </Typography>
            </Box>
            
            <Typography variant="caption" color="textSecondary">
              Server Time: {formatTimestamp(cacheStatus.serverTime)}
            </Typography>
          </Stack>
        </Box>
        
        <Button 
          variant="contained" 
          size="large" 
          startIcon={<ShoppingCartIcon />}
          onClick={(e) => {
            console.log('Button clicked');
            try {
              handleContinue(e);
            } catch (error) {
              console.error('Navigation error:', error);
              window.location.href = '/shop'; // Force navigation as last resort
            }
          }}
          sx={{ mt: 2, px: 4, py: 1.5 }}
          component="a"
          href="/shop"
        >
          Continue to Shop
        </Button>
      </Paper>
    </Container>
  );
};

export default KioskInitialize;
